import React from 'react';
import { MessageCircle, Mail, Phone, MapPin, User } from 'lucide-react';
import { useLang } from '../../context/LanguageContext';
import { useSiteConfig, resolveImageUrl } from '../../context/SiteConfigContext';

const ContactCard = ({ contact, lang, labels }) => {
  const role = contact.roles?.[lang] || '';
  const photoUrl = resolveImageUrl(contact.photo);
  const waText = encodeURIComponent(
    lang === 'fr' ? `Bonjour ${contact.name}, ` :
    lang === 'en' ? `Hi ${contact.name}, ` :
    lang === 'es' ? `Hola ${contact.name}, ` :
    `Hallo ${contact.name}, `
  );
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] overflow-hidden flex flex-col">
      <div className="p-6 flex gap-5 items-center border-b border-white/5">
        <div className="shrink-0 w-16 h-16 rounded-full overflow-hidden bg-white/10 grid place-items-center border border-white/10">
          {photoUrl ? (
            <img src={photoUrl} alt={contact.name} className="w-full h-full object-cover" />
          ) : (
            <User size={26} className="text-white/40" />
          )}
        </div>
        <div>
          <div className="font-serif text-xl text-white">{contact.name}</div>
          <div className="text-[12.5px] text-white/60">{role}</div>
        </div>
      </div>

      <div className="p-4 grid grid-cols-1 gap-2">
        <a
          href={`https://wa.me/${contact.whatsapp}?text=${waText}`}
          target="_blank"
          rel="noreferrer"
          className="group inline-flex items-center justify-between gap-3 rounded-md bg-[#25D366]/10 hover:bg-[#25D366]/20 border border-[#25D366]/30 px-3 py-2.5 transition-colors"
        >
          <span className="inline-flex items-center gap-2 text-[13px] text-white">
            <span className="grid place-items-center w-7 h-7 rounded-full bg-[#25D366] text-white"><MessageCircle size={13} /></span>
            {labels.whatsappLabel}
          </span>
          <span className="font-mono text-[11px] text-white/60">{contact.phone}</span>
        </a>
        <a href={`tel:+${contact.whatsapp}`} className="inline-flex items-center justify-between gap-3 rounded-md hover:bg-white/5 border border-white/10 px-3 py-2.5 transition-colors">
          <span className="inline-flex items-center gap-2 text-[13px] text-white/80">
            <span className="grid place-items-center w-7 h-7 rounded-full bg-white/5 text-[#e0b972]"><Phone size={13} /></span>
            {labels.phoneLabel}
          </span>
          <span className="font-mono text-[11px] text-white/50">{contact.phone}</span>
        </a>
        {contact.email && (
          <a href={`mailto:${contact.email}`} className="inline-flex items-center justify-between gap-3 rounded-md hover:bg-white/5 border border-white/10 px-3 py-2.5 transition-colors">
            <span className="inline-flex items-center gap-2 text-[13px] text-white/80">
              <span className="grid place-items-center w-7 h-7 rounded-full bg-white/5 text-[#e0b972]"><Mail size={13} /></span>
              {labels.emailLabel}
            </span>
            <span className="font-mono text-[11px] text-white/50 truncate max-w-[160px]">{contact.email}</span>
          </a>
        )}
      </div>
    </div>
  );
};

const Contact = () => {
  const { lang } = useLang();
  const { config } = useSiteConfig();
  if (!config) return null;
  const section = config.contactSection?.[lang] || {};
  const contacts = (config.contacts || []).filter((c) => (c.showInLangs || []).includes(lang));

  return (
    <section id="reserve" className="bg-[#0e2a24] text-white">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24">
        <p className="text-[11px] tracking-[0.25em] uppercase text-white/50">{section.overline}</p>
        <h2 className="mt-4 font-serif text-3xl md:text-5xl leading-[1.05] tracking-tight max-w-3xl">{section.title}</h2>
        <p className="mt-4 text-[15px] text-white/70 max-w-2xl">{section.lead}</p>

        <div className="mt-12 grid gap-5 grid-cols-1 md:grid-cols-2 max-w-4xl">
          {contacts.map((c) => (<ContactCard key={c.id} contact={c} lang={lang} labels={section} />))}
        </div>

        {section.locationValue && (
          <div className="mt-6 max-w-4xl rounded-lg border border-white/10 bg-white/[0.03] p-5">
            <div className="flex items-start gap-4">
              <span className="grid place-items-center w-10 h-10 rounded-full bg-white/5 text-[#e0b972] shrink-0"><MapPin size={16} /></span>
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-white/50">{section.locationLabel}</div>
                <div className="mt-1 font-serif text-lg">{section.locationValue}</div>
                <div className="mt-1 text-[13px] text-white/60">{section.locationExtra}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Contact;
